import type { BulletLegendItemInterface } from "@unovis/ts"
import type { Ref } from "vue"
import { useEventListener } from "@vueuse/core"
import { ref, watch } from "vue"

/**
 * Pure reducer for the unified legend-click interaction model, extracted so it
 * can be unit-tested without mounting Unovis (which can't mount under jsdom
 * without the deps.inline work tracked as a separate backlog item).
 *
 * One model for every chart type (phase-2 divergence #4 — previously Area
 * solo-isolated, Donut dimmed, Line/Bar were inert):
 *
 * - plain click on a series → **isolate** it (that series active, all others
 *   inactive); clicking the sole-active series again → **restore all**.
 * - shift-click → **toggle** just that one series.
 *
 * Returns a new array (never mutates the input) with each item's `inactive`
 * flag resolved. Unknown names are returned unchanged.
 */
export function computeLegendClick(
  items: BulletLegendItemInterface[],
  name: string | number | undefined,
  shiftKey: boolean,
): BulletLegendItemInterface[] {
  const target = items.find(i => i.name === name)
  if (!target) return items.map(i => ({ ...i }))

  if (shiftKey) {
    return items.map(i => (
      i.name === name ? { ...i, inactive: !i.inactive } : { ...i }
    ))
  }

  const activeCount = items.filter(i => !i.inactive).length
  const isOnlyActive = activeCount === 1 && !target.inactive

  if (isOnlyActive) {
    // The clicked series is the only one showing — restore the full set.
    return items.map(i => ({ ...i, inactive: false }))
  }

  // Isolate the clicked series.
  return items.map(i => ({ ...i, inactive: i.name !== name }))
}

/**
 * Shared legend state + interaction for every chart type. Owns the
 * `legendItems` model (kept in sync with reactive categories/colors while
 * preserving each series' toggled state) and the unified click handler
 * (isolate / restore-all / shift-toggle via {@link computeLegendClick}).
 *
 * @param categories Reactive series names (donut derives these from its data).
 * @param colors     Reactive resolved palette, index-aligned to `categories`.
 */
export function useChartLegend(
  categories: Ref<readonly string[]>,
  colors: Ref<readonly string[]>,
) {
  const legendItems = ref<BulletLegendItemInterface[]>([])

  // Rebuild items when categories or colors change, carrying over the
  // toggled (inactive) state by series name so a color/data refresh never
  // silently re-activates hidden series.
  watch([categories, colors], ([cats, cols]) => {
    legendItems.value = cats.map((name, i) => ({
      name,
      color: cols[i],
      inactive: legendItems.value.find(l => l.name === name)?.inactive ?? false,
    }))
  }, { immediate: true })

  // Shift held → toggle a single series instead of isolating.
  const shiftKeyPressed = ref(false)
  useEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Shift') shiftKeyPressed.value = true
  })
  useEventListener('keyup', (e: KeyboardEvent) => {
    if (e.key === 'Shift') shiftKeyPressed.value = false
  })

  function handleLegendItemClick(d: BulletLegendItemInterface) {
    legendItems.value = computeLegendClick(
      legendItems.value,
      d.name,
      shiftKeyPressed.value,
    )
  }

  return { legendItems, handleLegendItemClick }
}
